import type { CSSProperties } from "react";

/**
 * Crosshair brand mark — inlined SVG so it inherits currentColor.
 * Matches break-the-hidden-test-design-system/project/assets/mark.svg
 * but transparent background so we can drop it on any surface.
 *
 * Per design-system SKILL.md: do not redraw, do not stylize.
 */
export function BrandMark({
  size = 24,
  style,
  className,
  ariaLabel = "Break the Hidden Test",
}: {
  size?: number;
  style?: CSSProperties;
  className?: string;
  ariaLabel?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={ariaLabel}
      className={className}
      style={{ color: "var(--accent-amber)", ...style }}
    >
      {/* bracket corners */}
      <path d="M8 14 L8 8 L14 8" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="square" />
      <path d="M50 8 L56 8 L56 14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="square" />
      <path d="M56 50 L56 56 L50 56" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="square" />
      <path d="M14 56 L8 56 L8 50" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="square" />
      {/* reticle */}
      <circle cx="32" cy="32" r="13" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <circle cx="32" cy="32" r="2" fill="currentColor" />
      {/* cross */}
      <line x1="32" y1="13" x2="32" y2="19" stroke="currentColor" strokeWidth="1.5" />
      <line x1="32" y1="45" x2="32" y2="51" stroke="currentColor" strokeWidth="1.5" />
      <line x1="13" y1="32" x2="19" y2="32" stroke="currentColor" strokeWidth="1.5" />
      <line x1="45" y1="32" x2="51" y2="32" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

/**
 * Tick rail — a row of small vertical ticks used as a horizontal divider.
 * Lives just below status strips on every screen — instrument-panel idiom.
 */
export function TickRail({
  ticks = 24,
  className,
}: {
  ticks?: number;
  className?: string;
}) {
  return (
    <div
      className={["bht-tick-rail", className].filter(Boolean).join(" ")}
      aria-hidden
    >
      {Array.from({ length: ticks }).map((_, i) => (
        <span
          key={i}
          className={`bht-tick-rail__mark ${i % 4 === 0 ? "bht-tick-rail__mark--major" : ""}`}
        />
      ))}
    </div>
  );
}

/**
 * Instrument header strip — brand mark + wordmark + optional status text.
 * Used at the top of every screen to give the tactical-instrument framing.
 */
export type InstrumentHeaderProps = {
  /** Right-side status string in mono. Examples: "B3 · TRAPLINE · 02:14" */
  status?: string;
  /** Optional crumb shown left of the status — module label or section */
  context?: string;
  /** Hide brand wordmark text (useful on cramped screens) */
  compact?: boolean;
};

export function InstrumentHeader({
  status,
  context,
  compact = false,
}: InstrumentHeaderProps) {
  return (
    <div className="bht-instr-header">
      <div className="bht-instr-header__left">
        <BrandMark size={20} />
        {!compact && <span className="bht-instr-header__wordmark">BREAK·THE·HIDDEN·TEST</span>}
      </div>
      <div className="bht-instr-header__right">
        {context && <span className="bht-instr-header__context">{context}</span>}
        {status && <span className="bht-instr-header__status">{status}</span>}
      </div>
    </div>
  );
}
